import { HTMLAttributes, Ref, forwardRef } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { LinkComponent } from '../Banner/Banner'
import './Checkbox.css'

type TCheckbox = HTMLAttributes<HTMLInputElement>

export type CheckboxProps = {
  isChecked: boolean
  fieldName: string
  label: string
  onChange: (value: boolean) => void
  disabled?: boolean
  testId?: string
  required?: boolean
  required_front?: boolean
  labelClasses?: string
  checkboxClasses?: string
} & Omit<TCheckbox, 'onChange'>

const UncheckedIcon = () => (
  <svg
    data-testid="unchecked-icon"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="2.5"
      y="2.5"
      width="15"
      height="15"
      rx="2"
      stroke="#CFCFCF"
      strokeWidth="1.5"
      fill="#FFFFFF"
    />
  </svg>
)

const CheckedIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    data-testid="checked-icon"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="2.5"
      y="2.5"
      width="15"
      height="15"
      rx="2"
      fill={disabled ? '#A6A6A6' : '#0074E0'}
    />
    <path
      d="M6.25 10.25L8.75 12.75L13.75 7.25"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Checkbox = forwardRef(function Checkbox(
  {
    isChecked,
    fieldName,
    onChange,
    disabled = false,
    testId,
    required = false,
    required_front = false,
    labelClasses = '',
    checkboxClasses = '',
    label = '',
    className = '',
    ...props
  }: CheckboxProps,
  ref: Ref<HTMLLabelElement> | undefined
) {
  return (
    <label
      className={`${className} Checkbox-wrapper ${
        disabled ? 'Checkbox-wrapper--disabled' : ''
      }`}
      data-testid={testId || ''}
      htmlFor={fieldName}
      ref={ref}
    >
      <input
        data-testid="checkbox-input"
        type="checkbox"
        id={fieldName}
        checked={isChecked}
        onChange={({ target: { checked } }) => {
          onChange(checked)
        }}
        disabled={disabled}
        required={required || required_front}
        {...props}
      />
      <div
        data-testid="custom-checkbox"
        className={`Checkbox ${checkboxClasses}`}
        aria-hidden="true"
      >
        {isChecked ? <CheckedIcon disabled={disabled} /> : <UncheckedIcon />}
      </div>
      <div className="Checkbox__label-wrapper">
        {required_front && (
          <span
            data-testid="required-front"
            className="Checkbox__required_front"
          >
            *
          </span>
        )}
        <div className={`Checkbox__label ${labelClasses}`}>
          <Markdown
            components={{ a: LinkComponent }}
            rehypePlugins={[rehypeRaw]}
          >{`${label} ${
            required
              ? `<span data-testid="required" class="Checkbox__required">*</span>`
              : ''
          }`}</Markdown>
        </div>
      </div>
    </label>
  )
})

export default Checkbox
